import { Badge, BadgeProps } from '@material-ui/core';
import { BEHAVIOR } from 'components/twilio/constants';
import React, { FC, useMemo } from 'react';
import styled from 'styled-components';

const StyledBadge = styled(Badge)`
  & .ChatBadge-Badge {
    font-family: Lato, sans-serif;
    font-size: 10px;
    height: 16px;
    min-width: 16px;
    padding: 0 4px;
  }

  & .Live-Indicator {
    color: #44B700;
    background-color: #44B700;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 1px solid #FFF;
  }
`;

const OnlineDotWrapper: FC<{ displayOnlineDot: boolean }> = ({ displayOnlineDot, children }) => {
  if (!displayOnlineDot) return <>{children}</>;

  return (
    <StyledBadge
      {...defaultBadgeProps}
      badgeContent={displayOnlineDot}
      classes={{ badge: 'Live-Indicator' }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      variant="dot"
    >
      {children}
    </StyledBadge>
  );
};

const defaultBadgeProps: BadgeProps = {
  overlap: 'circle',
  max: BEHAVIOR.CHAT_BADGE_MAX,
  color: 'error',
  classes: { badge: 'ChatBadge-Badge' },
};

export interface ChatBadgeProps {
  unreadCount?: number,
  displayOnlineDot?: boolean,
}

const ChatBadge: FC<Omit<BadgeProps, 'badgeContent'> & ChatBadgeProps> = ({
  children,
  displayOnlineDot,
  unreadCount,
  ...badgeProps
}) => (
  <StyledBadge
    {...defaultBadgeProps}
    {...badgeProps}
    badgeContent={unreadCount}
  >
    <OnlineDotWrapper displayOnlineDot={displayOnlineDot}>
      {children}
    </OnlineDotWrapper>
  </StyledBadge>
);

export const MemoedChatBadge: FC<ChatBadgeProps> = React.memo(({
  children,
  displayOnlineDot,
  unreadCount,
}) => {
  const memoedChildren = useMemo(() => children, []);
  return (
    <StyledBadge
      {...defaultBadgeProps}
      badgeContent={unreadCount}
      color="error"
      classes={{ badge: 'ChatBadge-Badge' }}
    >
      <OnlineDotWrapper displayOnlineDot={displayOnlineDot}>
        {memoedChildren}
      </OnlineDotWrapper>
    </StyledBadge>
  );
}, (prevProps, nextProps) => (
  prevProps.unreadCount === nextProps.unreadCount
  && prevProps.displayOnlineDot === nextProps.displayOnlineDot
));

export default React.memo(ChatBadge);
